import { addDays, format, previousDay } from "date-fns"
import { treegridDateFormat } from "../../utils"

type MakeGanttBackgroundArgs = {
  weekendDays: number[] // Day of week numbers for weekend days, where 0 is Sunday, 1 is Monday etc
  fixedHolidays: string[] // Array of fixed holiday dates in format 'yyyy-MM-dd'
  recurringHolidays: string[] // Array of recurring holiday dates in format 'MM-dd'
}

// Creates pattern that renders a vertical line for each day in the Gantt chart
const createPatternForDailyVerticalLine = (): string => {
  const now = new Date()
  const formattedNow = format(now, treegridDateFormat)
  return `d#${formattedNow}#1`
}

// Given a day of week (0 for Sunday, 1 for Monday, etc), creates pattern that renders background for that day of week, repeating every week
const createPatternForWeeklyDayBackground = (dayOfWeek: number, refDate: Date = new Date()) => {
  const backgroundStartDate = previousDay(refDate, dayOfWeek)
  const backgroundEndDate = addDays(backgroundStartDate, 1)

  const formattedBackgroundStart = format(backgroundStartDate, treegridDateFormat)
  const formattedBackgroundEnd = format(backgroundEndDate, treegridDateFormat)

  return `w#${formattedBackgroundStart}~${formattedBackgroundEnd}#1`
}

// Given a fixed holiday date (in format 'yyyy-MM-dd'), creates pattern that renders background for that date
const createPatternForFixedHolidayBackground = (fixedHoliday: string) => {
  const holidayStartDate = new Date(fixedHoliday)
  if (isNaN(holidayStartDate.getTime())) {
    return ""
  }

  const holidayEndDate = addDays(holidayStartDate, 1)
  const formattedHolidayStart = format(holidayStartDate, treegridDateFormat)
  const formattedHolidayEnd = format(holidayEndDate, treegridDateFormat)

  return `#${formattedHolidayStart}~${formattedHolidayEnd}#1`
}

// Given a recurring holiday (in format 'MM-dd'), creates pattern that renders background for that date every year
const createPatternForRecurringHolidayBackground = (recurringHoliday: string) => {
  const currentYear = new Date().getFullYear()
  const holidayDate = new Date(`${currentYear}-${recurringHoliday}`)

  const patternForTheDate = createPatternForFixedHolidayBackground(format(holidayDate, "yyyy-MM-dd"))
  return patternForTheDate ? `y${patternForTheDate}` : ""
}

export const makeGanttBackground = (args: MakeGanttBackgroundArgs): string => {
  const { weekendDays, fixedHolidays, recurringHolidays } = args

  const dailyVerticalLinePattern = createPatternForDailyVerticalLine()
  const weekendDayPatterns = weekendDays.map((day) => createPatternForWeeklyDayBackground(day))
  const fixedHolidayPatterns = fixedHolidays.map((holiday) => createPatternForFixedHolidayBackground(holiday))
  const recurringHolidayPatterns = recurringHolidays.map((holiday) => {
    return createPatternForRecurringHolidayBackground(holiday)
  })

  const backgroundPatterns = [
    dailyVerticalLinePattern,
    ...weekendDayPatterns,
    ...fixedHolidayPatterns,
    ...recurringHolidayPatterns,
  ].filter((pattern) => pattern) // Filter out empty patterns

  if (backgroundPatterns.length < 0) return ""
  return backgroundPatterns.join(";") + ";"
}
